import { CenterCircle, CenterToFit, Close } from "@carbon/icons-react";
import { Button, IconButton, Tooltip } from "@carbon/react";
import { useInfiniteScrollTable } from "@flaminem/ui-components";
import React, { useMemo, useRef, useState } from "react";
import { FullScreen } from "react-full-screen";
import { generatePath, useNavigate } from "react-router-dom";

import { CaseArchivedTag } from "src/components/case/CaseArchivedTag";
import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import InlineError from "src/components/Errors/InlineError.tsx";
import { InfiniteScrollTable } from "src/components/Tables/InfiniteScrollTable.tsx";
import { defaultRowHeight } from "src/config/table-config";
import {
  useBuildCaseRows,
  useGetRelatedCasesColumns,
} from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.mappings.tsx";
import {
  RelatedWidgetConfiguration,
  ViewModeType,
} from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCases.typings.ts";
import { getAllIdentifiers } from "src/dynamic-view-renderer/Widgets/RelatedCases/relatedCasesPrepareQuery.ts";
import RelatedCasesToolbar from "src/dynamic-view-renderer/Widgets/RelatedCases/RelatedCasesToolbar.tsx";
import { useRelatedCasesTableURLParams } from "src/dynamic-view-renderer/Widgets/RelatedCases/useRelatedCasesURLParams.ts";
import ViewModePopover, {
  useViewModePopover,
} from "src/dynamic-view-renderer/Widgets/RelatedCases/ViewModePopover/ViewModePopover.tsx";
import useGetRelatedCases from "src/graphql/anti-fraud/queries/alerts/GetRelatedCasesQuery.ts";
import { useFullScreen } from "src/hooks/useFullScreen.ts";
import { useT } from "src/internationalization";
import { Element } from "src/types/element";
import { PropertyValue } from "src/types/property";

import { useBuildGraphForLayout } from "./Graph/graphData";
import RelatedCasesGraphComponent from "./Graph/RelatedCasesGraphComponent";

export type RelatedCasesWidgetProps = {
  data: {
    elements?: Element[];
    propertyValues?: PropertyValue[];
    caseId?: string;
    alertId: string;
    snapshotCreation: Date | null;
  };
  configuration: RelatedWidgetConfiguration;
};

const RelatedCasesWidget: React.FC<RelatedCasesWidgetProps> = ({
  data: alertCaseData,
  configuration,
}) => {
  const t = useT();
  const buildCaseRows = useBuildCaseRows();
  const navigate = useNavigate();

  const [selectedViewMode, setSelectedViewMode] = useState<ViewModeType>("Table");
  const {
    isPopoverOpen,
    setIsPopoverOpen,
    selectedAlgorithm,
    setSelectedAlgorithm,
    mergeRelatedCases,
    setMergeRelatedCases,
  } = useViewModePopover();

  const startButton = useRef<HTMLButtonElement>(null);
  const fitButton = useRef<HTMLButtonElement>(null);

  const { identifierValues, handleOnFiltersChange } = useRelatedCasesTableURLParams();
  const allIdentifiers = getAllIdentifiers(configuration, alertCaseData.elements);
  const selectedIdentifiers = useMemo(
    () => allIdentifiers.filter((identifier) => identifierValues.includes(identifier.value)),
    [identifierValues, allIdentifiers]
  );

  const currentCaseId = alertCaseData.caseId;
  const snapshotCreation = alertCaseData.snapshotCreation;

  const { pageSize, dimensions } = useDimensions();
  const { data, loading, refetch, error, hasError } = useGetRelatedCases(
    {
      caseId: currentCaseId!,
      options: {
        identifierValues: selectedIdentifiers,
        pagination: {
          limit: 50,
          offset: 0,
        },
      },
    },
    { skip: currentCaseId === undefined }
  );

  const { data: infiniteData, totalCount } = useInfiniteScrollTable(
    buildCaseRows(data?.cases ?? [], configuration),
    data?.pageInfo?.totalCount ?? null,
    data?.pageInfo?.limit ?? pageSize,
    data?.pageInfo?.offset ?? 0
  );

  const handleLoadMore = async (startIndex: number, stopIndex: number) => {
    if (!loading) {
      refetch({
        options: {
          identifierValues: selectedIdentifiers,
          pagination: {
            limit: stopIndex - startIndex + 1,
            offset: startIndex,
          },
        },
      });
    }
  };
  const getRelatedCasesColumns = useGetRelatedCasesColumns();

  const relatedCases = data?.cases ?? [];

  const { graphData, graphLayout, graphStyles } = useBuildGraphForLayout({
    algorithm: selectedAlgorithm,
    mergeRelatedCases,
    caseInfo: {
      caseId: alertCaseData.caseId,
      propertyValues: alertCaseData.propertyValues ?? [],
      elements: alertCaseData.elements ?? [],
      alertId: alertCaseData.alertId,
    },
    relatedCases,
    configuration,
  });

  const { handle: handleFullScreen } = useFullScreen();

  if (hasError) {
    return <InlineError message={error?.error?.message ?? ""} />;
  }

  return (
    <div>
      {snapshotCreation && <CaseArchivedTag date={snapshotCreation} />}
      {selectedViewMode === "Graph" && (
        <div>
          <FullScreen handle={handleFullScreen}>
            <RelatedCasesToolbar
              filters={{ identifierValues }}
              handleOnFiltersChange={handleOnFiltersChange}
              allIdentifiers={allIdentifiers}
              leftChildren={
                <>
                  <Button ref={startButton} kind="ghost" className="visible">
                    {t("widget.relatedCases.autoLayout")}
                  </Button>
                  <Tooltip label={t("widget.relatedCases.centerGraph")} align="bottom">
                    <IconButton
                      ref={fitButton}
                      kind="ghost"
                      label={t("widget.relatedCases.centerGraph")}
                    >
                      <CenterCircle />
                    </IconButton>
                  </Tooltip>
                </>
              }
            >
              <ViewModePopover
                isOpen={isPopoverOpen}
                onOpenState={setIsPopoverOpen}
                onSelectViewMode={setSelectedViewMode}
                selectedViewMode={selectedViewMode}
                selectedAlgorithm={selectedAlgorithm}
                onSelectedAlgorithm={setSelectedAlgorithm}
                mergeRelatedCases={mergeRelatedCases}
                onMergeRelatedCases={setMergeRelatedCases}
              />

              {!handleFullScreen.active && (
                <Tooltip label={t("general.fullScreen")} align="bottom-right">
                  <IconButton
                    kind="ghost"
                    label={t("general.fullScreen")}
                    onClick={handleFullScreen.enter}
                  >
                    <CenterToFit />
                  </IconButton>
                </Tooltip>
              )}
              {handleFullScreen.active && (
                <Tooltip label={t("general.actions.close")} align="bottom-right">
                  <IconButton
                    kind="ghost"
                    label={t("general.actions.close")}
                    onClick={handleFullScreen.exit}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              )}
            </RelatedCasesToolbar>
            <div
              className="bg-layer02"
              style={{
                width: "100%",
                height: handleFullScreen.active ? "100vh" : "calc(100vh - 150px)",
              }}
            >
              <RelatedCasesGraphComponent
                graphData={graphData}
                graphLayout={graphLayout}
                graphStyles={graphStyles}
                isLoading={loading}
                startButton={startButton}
                fitButton={fitButton}
                isFullScreen={handleFullScreen.active}
              />
            </div>
          </FullScreen>
        </div>
      )}
      {selectedViewMode === "Table" && (
        <DimensionedLayout>
          {dimensions?.width && (
            <InfiniteScrollTable
              id="related-cases-table"
              className={"flex-1"}
              totalCount={totalCount}
              rowHeight={defaultRowHeight}
              fetchMore={handleLoadMore}
              data={infiniteData ?? []}
              isLoading={loading}
              onRowClick={(row) => {
                row.data &&
                  navigate(
                    generatePath("/case/:caseTypeCode/:id", {
                      caseTypeCode: row.data.typeCode,
                      id: row.data.id,
                    })
                  );
              }}
              columns={getRelatedCasesColumns(
                configuration,
                alertCaseData?.propertyValues,
                alertCaseData?.elements
              )}
              pageSize={pageSize}
              toolbar={() => (
                <RelatedCasesToolbar
                  filters={{ identifierValues }}
                  handleOnFiltersChange={handleOnFiltersChange}
                  allIdentifiers={allIdentifiers}
                >
                  <ViewModePopover
                    isOpen={isPopoverOpen}
                    selectedViewMode={selectedViewMode}
                    selectedAlgorithm={selectedAlgorithm}
                    mergeRelatedCases={mergeRelatedCases}
                    onMergeRelatedCases={setMergeRelatedCases}
                    onSelectedAlgorithm={setSelectedAlgorithm}
                    onOpenState={setIsPopoverOpen}
                    onSelectViewMode={setSelectedViewMode}
                  />
                </RelatedCasesToolbar>
              )}
            />
          )}
        </DimensionedLayout>
      )}
    </div>
  );
};

export default RelatedCasesWidget;
